import { webPaths } from "@zplanem/web-paths"
import type React from "react"
import { Link, useLocation } from "react-router-dom"
import { cn } from "../../lib/utils"
import { useVisibleProjectTabs } from "../hooks/use-visible-project-tabs"
import { useVisiblePropertyTabs } from "../hooks/use-visible-property-tabs"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { useAuthenticatedUser } from "../user-context"
import { BrandMark } from "./brand-mark"
import { NotificationBell } from "./notification-bell"
import { projectTabPath } from "./project-tabs"
import { propertyTabPath } from "./property-tabs"
import {
	RAIL_CONTROL_CLASS,
	RAIL_ROW_EXPANDED_CLASS,
	RailTooltip,
} from "./rail-controls"
import {
	activeProjectTabKey,
	activePropertyTabKey,
	railContextOf,
} from "./sidebar-rail-model"
import { useSidebarState } from "./sidebar-state-context"
import { PROJECT_TAB_ICONS, PROPERTY_TAB_ICONS } from "./tab-icons"
import { ThemePill } from "./theme-pill"

// The wordmark is written once: the visible expanded text, the collapsed
// tooltip, and the collapsed accessible name must never drift (audit F25).
const BRAND_NAME = "zPLANEM"

// Row layout shared by nav items, the toggle, and footer entries so the
// collapsed (centered) and expanded (left-aligned, labeled) states stay in
// lockstep across every control. rail-controls.tsx owns both strings; the
// footer widgets (bell, theme pill) use the same two constants directly.
const rowClass = (expanded: boolean) =>
	expanded ? RAIL_ROW_EXPANDED_CLASS : RAIL_CONTROL_CLASS

const iconTileClass = (active: boolean) =>
	cn(
		"flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors group-focus-visible:ring-2 group-focus-visible:ring-ring/60",
		active
			? "text-foreground"
			: "text-muted-foreground group-hover:bg-accent/60 group-hover:text-foreground",
	)

const ActiveBar: React.FC = () => (
	<span
		data-testid="rail-active-indicator"
		aria-hidden="true"
		className="absolute left-0 top-1/2 h-8 w-[3px] -translate-y-1/2 bg-brand-navy-active dark:bg-ring"
	/>
)

interface RailItemProps {
	to: string
	active: boolean
	icon: React.ReactNode
	label: string
	expanded: boolean
}

const RailItem: React.FC<RailItemProps> = ({
	to,
	active,
	icon,
	label,
	expanded,
}) => (
	<Link
		to={to}
		aria-label={label}
		aria-current={active ? "page" : undefined}
		className={rowClass(expanded)}
	>
		{active ? <ActiveBar /> : null}
		<span aria-hidden="true" className={iconTileClass(active)}>
			{icon}
		</span>
		{expanded ? (
			<span
				className={cn(
					"truncate text-sm",
					active ? "font-semibold text-foreground" : "text-muted-foreground",
				)}
			>
				{label}
			</span>
		) : (
			<RailTooltip label={label} />
		)}
	</Link>
)

const FolderIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<path d="M2 5a1 1 0 0 1 1-1h3l1.5 1.5H13a1 1 0 0 1 1 1V12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5Z" />
	</svg>
)

const TaskIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<path d="M3 4.5h10" />
		<path d="M3 8h10" />
		<path d="M3 11.5h6" />
	</svg>
)

const SettingsIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-5 h-5"
		aria-hidden="true"
	>
		{/* Cog: toothed ring + hollow hub. The ring is what makes it read as a
		    cog — teeth radiating from a bare centre render as a sun. */}
		<circle cx="8" cy="8" r="5.2" />
		<circle cx="8" cy="8" r="1.8" />
		<path d="M13.2 8h1.3M8 13.2v1.3M2.8 8H1.5M8 2.8V1.5M11.68 11.68l.92.92M4.32 11.68l-.92.92M4.32 4.32 3.4 3.4M11.68 4.32l.92-.92" />
	</svg>
)

const ChevronsIcon: React.FC<{ direction: "left" | "right" }> = ({
	direction,
}) => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={cn("w-5 h-5", direction === "left" ? "" : "rotate-180")}
		aria-hidden="true"
	>
		<path d="M9 4 5 8l4 4" />
		<path d="M13 4 9 8l4 4" />
	</svg>
)

function initialsOf(firstName: string | null, lastName: string | null): string {
	const joined = [firstName?.[0], lastName?.[0]].filter(Boolean).join("")
	return joined.toUpperCase() || "?"
}

const PropertyRailNav: React.FC<{
	propertyId: string
	pathname: string
	expanded: boolean
}> = ({ propertyId, pathname, expanded }) => {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const tabs = useVisiblePropertyTabs(propertyId)
	const active = activePropertyTabKey(pathname, propertyId)
	return (
		<>
			{tabs.map((tab) => {
				const Icon = PROPERTY_TAB_ICONS[tab.key]
				return (
					<RailItem
						key={tab.key}
						to={propertyTabPath(propertyId, tab)}
						active={tab.key === active}
						icon={<Icon />}
						label={t.properties.tabs[tab.key]}
						expanded={expanded}
					/>
				)
			})}
		</>
	)
}

const ProjectRailNav: React.FC<{
	propertyId: string
	projectId: string
	pathname: string
	expanded: boolean
}> = ({ propertyId, projectId, pathname, expanded }) => {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const tabs = useVisibleProjectTabs(propertyId)
	const active = activeProjectTabKey(pathname, propertyId, projectId)
	return (
		<>
			{tabs.map((tab) => {
				const Icon = PROJECT_TAB_ICONS[tab.key]
				return (
					<RailItem
						key={tab.key}
						to={projectTabPath(propertyId, projectId, tab)}
						active={tab.key === active}
						icon={<Icon />}
						label={t.projects.tabs[tab.key]}
						expanded={expanded}
					/>
				)
			})}
		</>
	)
}

/**
 * Desktop sidebar: collapses to the 88px icon rail or expands to the 264px
 * labeled menu (new-designs). The expanded/collapsed choice is persisted and
 * shared via SidebarStateProvider; the shell's main padding tracks it. Mobile
 * chrome is untouched.
 */
export const Sidebar: React.FC = () => {
	const { profile } = useAuthenticatedUser()
	const { locale } = useLocale()
	const location = useLocation()
	const { expanded, toggle } = useSidebarState()
	const t = getTranslations(locale)

	const path = location.pathname
	const railContext = railContextOf(path)
	const propertiesActive = path === "/dashboard" || path === "/properties/new"
	const myTicketsActive = path === webPaths.myTickets()
	const profileActive = path === "/profile"
	const settingsActive = path === webPaths.settings()

	const fullName = [profile.firstName, profile.lastName]
		.filter(Boolean)
		.join(" ")
	const roleLabel = t.userMainRoles[profile.mainRole]

	return (
		<aside
			className={cn(
				"hidden md:flex fixed top-0 left-0 h-screen bg-card border-r border-border flex-col z-40 transition-[width] duration-200 motion-reduce:transition-none",
				expanded ? "w-rail-expanded" : "w-rail",
			)}
		>
			<Link
				to={webPaths.dashboard()}
				// Expanded: the visible wordmark IS the accessible name
				// (WCAG 2.5.3). Collapsed: no visible text, so name it.
				aria-label={expanded ? undefined : BRAND_NAME}
				className={cn(
					"group relative flex h-14 w-full shrink-0 items-center border-b border-border outline-none focus-visible:ring-2 focus-visible:ring-ring/60",
					expanded ? "justify-start gap-2 px-5" : "justify-center",
				)}
			>
				<BrandMark className="h-9 w-9 shrink-0" />
				{expanded ? (
					<span className="text-lg font-bold tracking-tight text-foreground">
						{BRAND_NAME}
					</span>
				) : (
					<RailTooltip label={BRAND_NAME} />
				)}
			</Link>

			{/*
			  The nav is the only variable-height region (2 global entries vs 7
			  project tabs), so it is the one that scrolls: `min-h-0` lets it
			  shrink below its content and `overflow-y-auto` scrolls the excess,
			  which keeps the brand, the toggle, and the whole footer — down to
			  the last row, Settings — pinned and reachable on short viewports
			  (a `fixed` rail cannot be page-scrolled).

			  A scroll container clips on BOTH axes, and the collapsed rows hang
			  their label outside the 88px rail (`absolute left-full`). So when
			  collapsed the clip edge is pushed past the widest tooltip with
			  right padding that is pulled straight back out of the layout
			  (`pr-56 -mr-56`: rows stay 88px, the clip box is 312px), the
			  scrollbar that would then paint 224px into the page is hidden, and
			  the transparent overhang lets pointer events through so it can
			  never swallow a click on the content beside the rail.
			*/}
			<nav
				aria-label={t.sidebar.navLabel}
				className={cn(
					"flex min-h-0 flex-col gap-1 overflow-y-auto pt-3",
					!expanded &&
						"pointer-events-none -mr-56 pr-56 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>*]:pointer-events-auto",
				)}
			>
				{railContext.projectId && railContext.propertyId ? (
					<ProjectRailNav
						propertyId={railContext.propertyId}
						projectId={railContext.projectId}
						pathname={path}
						expanded={expanded}
					/>
				) : railContext.propertyId ? (
					<PropertyRailNav
						propertyId={railContext.propertyId}
						pathname={path}
						expanded={expanded}
					/>
				) : (
					<>
						<RailItem
							to={webPaths.dashboard()}
							active={propertiesActive}
							icon={<FolderIcon />}
							label={t.sidebar.properties}
							expanded={expanded}
						/>
						<RailItem
							to={webPaths.myTickets()}
							active={myTicketsActive}
							icon={<TaskIcon />}
							label={t.sidebar.myTickets}
							expanded={expanded}
						/>
					</>
				)}
			</nav>

			<div className="flex-1" />

			{/*
			  Collapse / expand toggle, above the footer divider. No
			  `aria-expanded`: nothing is hidden or revealed — every nav item
			  keeps the same accessible name in both states, only the rail's
			  width and the visible labels change. The button's own name
			  ("Collapse menu" / "Expand menu") already carries the state, so
			  `aria-expanded` would announce it a second time against a
			  referent assistive tech cannot observe.
			*/}
			<button
				type="button"
				onClick={toggle}
				aria-label={expanded ? t.sidebar.collapseMenu : t.sidebar.expandMenu}
				className={cn(rowClass(expanded), "shrink-0")}
			>
				<span aria-hidden="true" className={iconTileClass(false)}>
					<ChevronsIcon direction={expanded ? "left" : "right"} />
				</span>
				{expanded ? (
					<span className="truncate text-sm text-muted-foreground">
						{t.sidebar.collapseMenu}
					</span>
				) : (
					<RailTooltip label={t.sidebar.expandMenu} />
				)}
			</button>

			<div className="flex shrink-0 flex-col gap-2 border-t border-border pt-3 pb-4">
				<NotificationBell placement="sidebar" expanded={expanded} />
				<ThemePill compact expanded={expanded} />

				<Link
					to={webPaths.profile()}
					// Expanded: the visible name/role block IS the accessible
					// name (WCAG 2.5.3 label-in-name) — an aria-label here would
					// replace it with "Profile", which the visible text does not
					// contain. Collapsed: icon only, so name it.
					aria-label={expanded ? undefined : t.sidebar.profile}
					aria-current={profileActive ? "page" : undefined}
					className={rowClass(expanded)}
				>
					{profileActive ? <ActiveBar /> : null}
					<span
						aria-hidden="true"
						className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold group-focus-visible:ring-2 group-focus-visible:ring-ring/60"
					>
						{initialsOf(profile.firstName, profile.lastName)}
					</span>
					{expanded ? (
						<span className="flex min-w-0 flex-col">
							<span className="truncate text-sm font-medium text-foreground">
								{fullName || t.sidebar.profile}
							</span>
							{roleLabel ? (
								<span className="truncate text-xs text-muted-foreground">
									{roleLabel}
								</span>
							) : null}
						</span>
					) : (
						<RailTooltip
							label={
								fullName
									? `${t.sidebar.profile} — ${fullName}${roleLabel ? ` · ${roleLabel}` : ""}`
									: t.sidebar.profile
							}
						/>
					)}
				</Link>

				<Link
					to={webPaths.settings()}
					// Expanded: the visible text IS the accessible name
					// (WCAG 2.5.3 label-in-name) — see the Profile row's
					// comment above for why aria-label is omitted here too.
					aria-label={expanded ? undefined : t.sidebar.settings}
					aria-current={settingsActive ? "page" : undefined}
					className={rowClass(expanded)}
				>
					{settingsActive ? <ActiveBar /> : null}
					<span aria-hidden="true" className={iconTileClass(settingsActive)}>
						<SettingsIcon />
					</span>
					{expanded ? (
						<span className="truncate text-sm text-muted-foreground">
							{t.sidebar.settings}
						</span>
					) : (
						<RailTooltip label={t.sidebar.settings} />
					)}
				</Link>
			</div>
		</aside>
	)
}
